import { of } from 'rxjs'
import { map, pluck, mergeMap, filter, finalize } from 'rxjs/operators'
import { Epic } from 'redux-observable'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { EpicDependencies, RootState } from 'src/state/types'
import { ENDPOINTS } from './constants'
import { PartyEvent, events } from './state'
import * as selectors from './selectors'
import { isBulkFetchRedundant, isFullFetchRedundant, trackBulkFetch, trackFullFetch } from './inFlight'
import { TPartyPayload } from './types'
import { $TSFixMe } from 'types/ts-migrate'

type PartyEpic = Epic<PartyEvent, PartyEvent, RootState, EpicDependencies>

export const apiPartyQuickSearch: PartyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.quickSearch_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.QUICK_SEARCH, state$, action.payload).pipe(
        pluck('response'),
        map((results: TPartyPayload[]) => events.quickSearch_success({ results, params: action.payload })),
        catchRestError(action),
      ),
    ),
    tag('party/epics/apiPartyQuickSearch'),
  )

/**
 * Note there is deliberately no "already in state" guard here, unlike the bulk fetch below.
 * party_request doubles as a refresh — callers dispatch it on mount to pull current data for
 * a contact/owner/tenant — and quickSearch_success writes partial parties into the same
 * entity map, so "in state" does not imply "complete and current". Concurrent requests for
 * the same id are dropped though — see ./inFlight.
 */
export const apiFetchParty: PartyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.party_request.type)),
    filter(action => !isFullFetchRedundant(action.payload)),
    mergeMap(action => {
      const releaseInFlight = trackFullFetch(action.payload)
      return get(ENDPOINTS.PARTY_ENTITY, state$, { id: action.payload }).pipe(
        pluck('response'),
        map((e: TPartyPayload) =>
          events.party_success({ ...e, id: (e.company ?? e.person).id }, { id: action.payload }),
        ),
        catchRestError(action),
        finalize(releaseInFlight),
      )
    }),
    tag('party/epics/apiFetchParty'),
  )

export const apiCreateParty: PartyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.createParty_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.PARTY_BASE, state$, action.payload, undefined).pipe(
        pluck('response'),
        map((response: TPartyPayload) => events.createParty_success(response, action.meta)),
        catchRestError(action),
      ),
    ),
    tag('party/epics/apiCreateParty'),
  )

export const apiUpdateParty: PartyEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateParty_request.type)),
    mergeMap(action => {
      const { id, values } = action.payload
      return put(ENDPOINTS.PARTY_ENTITY, state$, values, { id }).pipe(
        pluck('response'),
        map((response: TPartyPayload) => events.updateParty_success(response)),
        catchRestError(action),
      )
    }),
    tag('party/epics/apiUpdateParty'),
  )

export const apiFetchPartyTags: PartyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.partyTags_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.PARTY_TAGS, state$, {}).pipe(
        pluck('response'),
        map(events.partyTags_success),
        catchRestError(action),
      ),
    ),
    tag('party/epics/apiFetchPartyTags'),
  )

export const apiFetchAccounts: PartyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.accounts_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.ACCOUNTS, state$, action.payload).pipe(
        pluck('response'),
        map(events.accounts_success),
        catchRestError(action),
      ),
    ),
    tag('party/epics/apiFetchAccounts'),
  )

export const fetchPartiesIfNotInState: PartyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.parties_request.type)),
    mergeMap(action => {
      const partyIds: string[] = Array.from(new Set(action.payload || []))
      // The in-state guard on its own is useless for requests dispatched in the same tick
      // (nothing has resolved yet, so every one of them sees an empty store and fires its
      // own POST); isBulkFetchRedundant closes that window.
      const unfetchedIds = partyIds.filter(
        id => !!id && !selectors.getPartyById(state$.value)(id) && !isBulkFetchRedundant(id),
      )
      // Always answer the request. Returning EMPTY here left apiState's `partyApi/parties`
      // loading flag stuck at true, with no success/error to ever reset it. The empty meta
      // is intentional: ids skipped as redundant are released by the request that owns them.
      if (unfetchedIds.length === 0) return of(events.parties_success([], { ids: [] }))
      const releaseInFlight = trackBulkFetch(unfetchedIds)
      return post(ENDPOINTS.PARTIES_BULK, state$, { ids: unfetchedIds }, undefined).pipe(
        pluck('response'),
        map((response: TPartyPayload[]) => events.parties_success(response, { ids: unfetchedIds })),
        catchRestError(action),
        finalize(releaseInFlight),
      )
    }),
    tag('party/epics/fetchPartiesIfNotInState'),
  )

export const apiSuspenseAccountFuzzyMatches: PartyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.suspenseAccountFuzzyMatches_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.BAD_REFERENCE_FUZZY_MATCHES, state$, action.payload).pipe(
        pluck('response'),
        map((res: $TSFixMe) => events.suspenseAccountFuzzyMatches_success(res, action.payload)),
        catchRestError(action),
      ),
    ),
    tag('party/epics/apiSuspenseAccountFuzzyMatches'),
  )

export const apiBulkImport: PartyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.bulkImport_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.BULK_IMPORT, state$, action.payload, undefined).pipe(
        pluck('response'),
        map(events.bulkImport_success),
        catchRestError(action),
      ),
    ),
    tag('party/epics/apiBulkImport'),
  )
